import clsx from 'clsx';
import { type LucideIcon, Check } from 'lucide-react';
import { Fragment } from 'react';

import { Icon } from '@/react/components/Icon';

import { BadgeIcon } from '@@/BadgeIcon';
import { getFeatureDetails } from '@@/BEFeatureIndicator/utils';

import styles from './BoxSelectorItem.module.css';
import { BoxSelectorOption, Value } from './types';
import { LimitedToBeIndicator } from './LimitedToBeIndicator';
import { BoxOption } from './BoxOption';
import { LogoIcon } from './LogoIcon';

type Props<T extends Value> = {
  option: BoxSelectorOption<T>;
  radioName: string;
  disabled?: boolean;
  tooltip?: string;
  onSelect(value: T, limitedToBE: boolean): void;
  isSelected(value: T): boolean;
  type?: 'radio' | 'checkbox';
  slim?: boolean;
  checkIcon?: LucideIcon;
};

export function BoxSelectorItem<T extends Value>({
  radioName,
  option,
  onSelect = () => {},
  disabled,
  tooltip,
  type = 'radio',
  isSelected,
  slim = false,
  checkIcon = Check,
}: Props<T>) {
  const { limitedToBE = false, url: featureUrl } = getFeatureDetails(
    option.feature
  );

  const beIndicatorTooltipId = `box-selector-item-${radioName}-${option.id}-limited`;

  const ContentBox = slim ? 'div' : Fragment;

  return (
    <BoxOption
      className={clsx(styles.boxSelectorItem, {
        [styles.business]: limitedToBE,
        [styles.limited]: limitedToBE,
      })}
      radioName={radioName}
      option={option}
      isSelected={isSelected}
      disabled={isDisabled()}
      onSelect={(value) => onSelect(value, limitedToBE)}
      tooltip={tooltip}
      type={type}
      checkIcon={checkIcon}
    >
      {limitedToBE && (
        <LimitedToBeIndicator
          tooltipId={beIndicatorTooltipId}
          url={featureUrl}
        />
      )}
      <div
        className={clsx('flex gap-2', {
          'opacity-30': limitedToBE,
          'h-full flex-col justify-start': !slim,
          'slim items-center': slim,
        })}
      >
        {renderIcon()}
        <ContentBox>
          <div className={styles.header}>{option.label}</div>
          <div className="mb-0">{option.description}</div>
        </ContentBox>
      </div>
    </BoxOption>
  );

  function isDisabled() {
    return disabled || (limitedToBE && option.disabledWhenLimited);
  }

  function renderIcon() {
    if (!option.icon) {
      return null;
    }

    let renderedIcon;
    if (option.iconType === 'badge') {
      renderedIcon = (
        <BadgeIcon icon={option.icon} iconClass={option.iconClass} />
      );
    } else if (option.iconType === 'raw') {
      renderedIcon = (
        <Icon
          icon={option.icon}
          className={clsx(styles.icon, option.iconClass, '!flex items-center')}
        />
      );
    } else {
      renderedIcon = (
        <LogoIcon icon={option.icon} iconClass={option.iconClass} />
      );
    }

    return (
      <div className={clsx(styles.imageContainer, 'flex items-start')}>
        {renderedIcon}
      </div>
    );
  }
}
